import React from 'react';
import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  fontSize,
  lineHeight,
  space,
  themeGet,
  width,
  zIndex,
} from 'styled-system';

import { StateType } from '../../../enums/StateType';
import { getForwardPropOpts } from '../../../theme/helpers/forward-props';
import { boxSizing } from '../../../theme/system-utilities';
import type { ThemedComponent, WithTheme } from '../../../theme/theme';
import { TYPOGRAPHY_BR1_FONT_SIZE, TYPOGRAPHY_BR1_LINE_HEIGHT } from '../../Typography';
import { TRANSITION_CSS } from './constants';
import { ThemeBorderColor, ThemeTextColor } from './enums';
import type { InputProps, InputStyledProps } from './types';
import { getStateBorderColor, getStateTextColor, getStateType } from './utils';

export type InputInnerProps = Omit<InputProps, 'label' | 'inputRef'>;

const ICON_SPACING_RIGHT = '30px';

export const applyInputCSS = (props: WithTheme<InputProps>) => {
  const { hasError, hasWarning, hasSuccess, isLoading, withIcon } = props;
  const stateType = getStateType({
    hasError,
    hasWarning,
    hasSuccess,
    isLoading,
  });

  return (
    stateType !== StateType.Default &&
    withIcon &&
    css`
      padding-right: ${ICON_SPACING_RIGHT};
    `
  );
};

export const inputFocusCSS = (props: WithTheme) => css`
  border-color: transparent;
  outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')(props)};
`;

export const inputDisabledCSS = (props: WithTheme) => css`
  color: ${themeGet(`colors.${ThemeTextColor.Placeholder}`)(props)};
  background-color: ${themeGet('colors.sys.neutral.background.hover')(props)};
  border-color: ${themeGet(`colors.${ThemeBorderColor.Default}`)(props)};
`;

export const applyInputWithStateCSS = (props: WithTheme<InputProps>) => {
  const { hasError, hasWarning, hasSuccess, isLoading, value } = props;
  const stateType = getStateType({
    hasError,
    hasWarning,
    hasSuccess,
    isLoading,
  });
  const themeTextColor = getStateTextColor(stateType, value);
  const themeBorderColor = getStateBorderColor(stateType, value);

  return css`
    color: ${themeGet(`colors.${themeTextColor}`)(props)};
    border-color: ${themeGet(`colors.${themeBorderColor}`)(props)};
  `;
};

const UnstyledInnerInput: ThemedComponent<InputInnerProps, InputStyledProps> = styled(
  'input',
  getForwardPropOpts([])
)`
  ${width};
  ${fontSize};
  ${lineHeight};
  ${space};
  ${boxSizing};
  ${borderRadius};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border}
  ${zIndex};

  ${TRANSITION_CSS};
  transition:
    outline,
    color 0s;

  &::placeholder {
    ${TRANSITION_CSS};
    color: transparent;
  }

  &:focus::placeholder {
    color: ${themeGet(`colors.${ThemeTextColor.Placeholder}`)};
  }

  &:not(:disabled) {
    &:active,
    &:focus {
      ${inputFocusCSS};
    }
  }

  &:disabled {
    ${inputDisabledCSS};
  }

  &[aria-invalid='true'] {
    padding-right: ${themeGet('space.sp_32')}px;
  }

  ${applyInputCSS};
  ${applyInputWithStateCSS};

  ${({ readOnly }) => readOnly && inputDisabledCSS};
`;

const InnerInput = (props: ComponentProps<typeof UnstyledInnerInput>) => (
  <UnstyledInnerInput
    width={1}
    // Not possible to reuse Typography component here.
    fontSize={TYPOGRAPHY_BR1_FONT_SIZE}
    lineHeight={TYPOGRAPHY_BR1_LINE_HEIGHT}
    mt="sp_0"
    mb={['sp_16', null, 'sp_0']}
    px="sp_16"
    py="sp_8"
    boxSizing="border-box"
    borderStyle="solid"
    borderWidth="small"
    borderRadius="small"
    zIndex={0}
    {...props}
  />
);

export default InnerInput;
